import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import orendaService from "../../services/orenda.service";

// Transactions screen — chronological list of the user's activity with
// filters, pagination, and a one-click PDF/CSV statement download.
//
// Orenda's transaction row has mixed casing (PaymentId, Payer, CardAuth
// are PascalCase; beneficiary_details, payment_details are snake_case;
// everything else is camelCase). We copy the shape verbatim on the
// backend and just pick-through here.
//
// Rows the service can't render are DROPPED from the page and from
// `total`, so pagination counts always reflect only what we can show.

const parseListResponse = (response) => {
  const rawStr = response?.payload?.errorMessage ?? response?.errorMessage;
  let raw = null;
  if (typeof rawStr === "string") {
    try { raw = JSON.parse(rawStr); } catch { raw = null; }
  }
  const typed = response?.payload?.data ?? response?.data ?? null;
  const data =
    (Array.isArray(raw?.data) && raw.data) ||
    (Array.isArray(typed?.data) && typed.data) ||
    (Array.isArray(typed) && typed) ||
    [];
  const total = raw?.total ?? typed?.total ?? (Array.isArray(data) ? data.length : 0);
  const page = raw?.page ?? typed?.page ?? 1;
  const limit = raw?.limit ?? typed?.limit ?? data.length;
  return { data, total, page, limit };
};

const parseStatementResponse = (response) => {
  const rawStr = response?.payload?.errorMessage ?? response?.errorMessage;
  if (typeof rawStr === "string") {
    try {
      const parsed = JSON.parse(rawStr);
      if (parsed?.data?.dataURI) return parsed.data.dataURI;
    } catch { /* fall through */ }
  }
  return (
    response?.payload?.data?.dataURI ??
    response?.data?.dataURI ??
    null
  );
};

// Row display helpers.
const otherSideName = (t) => {
  // IN rows carry the Payer; OUT rows carry the beneficiary.
  if (t?.direction === "IN") {
    const payerName = t?.payment_details?.details?.payerName;
    if (payerName) return payerName;
    return t?.Payer?.Iban || t?.Payer?.AccountNumber || "Received";
  }
  const bene = t?.beneficiary_details;
  if (bene?.name) return bene.name;
  // Card spend lives on CardAuth.merchantName.
  const merchant = t?.CardAuth?.merchantName || t?.CardAuth?.merchant;
  if (merchant) return merchant;
  const ident = Array.isArray(bene?.identifiers) && bene.identifiers[0];
  if (ident?.iban) return ident.iban;
  if (ident?.accountNumber) return ident.accountNumber;
  return "Payment";
};

const subLine = (t) => {
  const bits = [];
  if (t?.CardAuth) bits.push("Card");
  else if (t?.direction === "IN") bits.push("Received");
  else bits.push("Sent");
  if (t?.isFee) bits.push("Fee");
  if (t?.uniqueRef) bits.push(`Ref ${t.uniqueRef}`);
  return bits.filter(Boolean).join(" · ");
};

const formatAmount = (amount, currency, direction) => {
  if (amount == null) return "—";
  const n = typeof amount === "number" ? amount : Number(amount);
  if (Number.isNaN(n)) return `${amount} ${currency || ""}`.trim();
  const sign = direction === "IN" ? "+" : direction === "OUT" ? "−" : "";
  return `${sign}${n.toFixed(2)} ${currency || ""}`.trim();
};

const formatDate = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleString(undefined, {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch { return ""; }
};

// Group rows by calendar day for a cleaner read.
const groupByDay = (list) => {
  const groups = new Map();
  for (const t of list) {
    const d = t?.transactionDate ? new Date(t.transactionDate) : null;
    const key = d && !Number.isNaN(d.getTime()) ? d.toDateString() : "—";
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(t);
  }
  // Keep insertion order (API already sorts desc).
  return Array.from(groups.entries());
};

const dayLabel = (key) => {
  if (key === "—") return "Earlier";
  try {
    const d = new Date(key);
    if (Number.isNaN(d.getTime())) return key;
    const today = new Date();
    const yest = new Date();
    yest.setDate(today.getDate() - 1);
    if (d.toDateString() === today.toDateString()) return "Today";
    if (d.toDateString() === yest.toDateString()) return "Yesterday";
    return d.toLocaleDateString(undefined, {
      weekday: "short",
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  } catch { return key; }
};

const PAGE_SIZE = 25;

const OrendaTransactions = () => {
  const [phase, setPhase] = useState("loading"); // loading | ready | error
  const [list, setList] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [error, setError] = useState(null);

  const [filters, setFilters] = useState({
    direction: "", // "" | "IN" | "OUT"
    startDate: "",
    endDate: "",
    search: "", // mapped to `reference` (comma-separated also works per docs)
  });

  const [stmtOpen, setStmtOpen] = useState(false);
  const [stmtBusy, setStmtBusy] = useState(false);
  const [stmtError, setStmtError] = useState(null);

  const requestIdRef = useRef(0);

  const load = useCallback(
    async (nextPage = page) => {
      setError(null);
      setPhase("loading");
      const id = ++requestIdRef.current;
      try {
        const resp = await orendaService.listTransactionsOrenda({
          page: nextPage,
          limit: PAGE_SIZE,
          sortBy: "dateTime",
          sortOrder: "desc",
          direction: filters.direction || undefined,
          startDate: filters.startDate || undefined,
          endDate: filters.endDate || undefined,
          reference: filters.search ? filters.search.trim() : undefined,
        });
        // Drop stale responses in case the user mashes filters.
        if (id !== requestIdRef.current) return;
        const parsed = parseListResponse(resp);
        setList(parsed.data);
        setTotal(parsed.total);
        setPage(parsed.page || nextPage);
        setPhase("ready");
      } catch (err) {
        if (id !== requestIdRef.current) return;
        setError(
          err?.response?.data?.errorMessage ||
            err?.message ||
            "Couldn't load transactions."
        );
        setPhase("error");
      }
    },
    [filters, page]
  );

  // Reload on filter changes — reset to page 1.
  useEffect(() => {
    load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const totalPages = Math.max(1, Math.ceil((total || 0) / PAGE_SIZE));
  const grouped = useMemo(() => groupByDay(list), [list]);

  const resetFilters = () =>
    setFilters({ direction: "", startDate: "", endDate: "", search: "" });

  const anyFilterActive =
    !!filters.direction || !!filters.startDate || !!filters.endDate || !!filters.search;

  return (
    <section className="orenda-benes orenda-txn-screen">
      <header className="orenda-benes-head">
        <div>
          <div className="orenda-eyebrow">Transactions</div>
          <h1 className="orenda-benes-title">Your activity</h1>
          <p className="orenda-benes-sub">
            Everything in and out — bank transfers, card spend, fees. Newest
            first. Download a monthly or custom-range statement any time.
          </p>
        </div>
        <button
          type="button"
          className="orenda-hero-cta orenda-hero-cta--secondary"
          onClick={() => {
            setStmtError(null);
            setStmtOpen(true);
          }}
        >
          <StatementIcon />
          Download statement
        </button>
      </header>

      <div className="orenda-txn-filters">
        <div className="orenda-benes-segmented orenda-txn-dir">
          <button
            type="button"
            className={filters.direction === "" ? "is-active" : ""}
            onClick={() => setFilters((f) => ({ ...f, direction: "" }))}
          >
            All
          </button>
          <button
            type="button"
            className={filters.direction === "IN" ? "is-active" : ""}
            onClick={() => setFilters((f) => ({ ...f, direction: "IN" }))}
          >
            Money in
          </button>
          <button
            type="button"
            className={filters.direction === "OUT" ? "is-active" : ""}
            onClick={() => setFilters((f) => ({ ...f, direction: "OUT" }))}
          >
            Money out
          </button>
        </div>

        <div className="orenda-txn-date-range">
          <label>
            <span>From</span>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) =>
                setFilters((f) => ({ ...f, startDate: e.target.value }))
              }
            />
          </label>
          <label>
            <span>To</span>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) =>
                setFilters((f) => ({ ...f, endDate: e.target.value }))
              }
            />
          </label>
        </div>

        <input
          type="text"
          className="orenda-send-search orenda-txn-search"
          placeholder="Search a reference…"
          value={filters.search}
          onChange={(e) =>
            setFilters((f) => ({ ...f, search: e.target.value.slice(0, 80) }))
          }
        />

        {anyFilterActive && (
          <button
            type="button"
            className="orenda-btn-ghost orenda-txn-reset"
            onClick={resetFilters}
          >
            Reset
          </button>
        )}
      </div>

      {phase === "loading" && (
        <div className="orenda-benes-card orenda-benes-empty">Loading…</div>
      )}

      {phase === "error" && (
        <div className="orenda-benes-card orenda-benes-empty">
          <p>{error}</p>
          <button
            type="button"
            className="orenda-btn-primary"
            onClick={() => load(page)}
          >
            Try again
          </button>
        </div>
      )}

      {phase === "ready" && list.length === 0 && (
        <div className="orenda-benes-card orenda-benes-empty">
          <h3>No transactions found</h3>
          <p>
            {anyFilterActive
              ? "Try widening the filters or clearing them."
              : "Once money moves through your account it'll show up here."}
          </p>
        </div>
      )}

      {phase === "ready" && list.length > 0 && (
        <>
          {grouped.map(([key, rows]) => (
            <div key={key} className="orenda-txn-group">
              <div className="orenda-txn-group-head">{dayLabel(key)}</div>
              <ul className="orenda-benes-list orenda-txn-list">
                {rows.map((t) => (
                  <li key={t.id} className="orenda-benes-row orenda-txn-row">
                    <div
                      className={`orenda-txn-dir-icon orenda-txn-dir-icon--${
                        t.direction === "IN" ? "in" : "out"
                      }`}
                      aria-hidden
                    >
                      {t.direction === "IN" ? "↓" : "↑"}
                    </div>
                    <div className="orenda-benes-row-main">
                      <div className="orenda-benes-row-name">
                        {otherSideName(t)}
                      </div>
                      <div className="orenda-benes-row-account orenda-pay-row-sub">
                        <span>{formatDate(t.transactionDate)}</span>
                        <span className="orenda-pay-ref">{subLine(t)}</span>
                      </div>
                    </div>
                    <div className="orenda-pay-row-right">
                      <div
                        className={`orenda-pay-amount orenda-txn-amount--${
                          t.direction === "IN" ? "in" : "out"
                        }`}
                      >
                        {formatAmount(t.amount, t.currency, t.direction)}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          {totalPages > 1 && (
            <div className="orenda-txn-pager">
              <button
                type="button"
                className="orenda-btn-ghost"
                disabled={page <= 1 || phase === "loading"}
                onClick={() => load(page - 1)}
              >
                Previous
              </button>
              <span className="orenda-txn-pager-pos">
                Page {page} of {totalPages} — {total} total
              </span>
              <button
                type="button"
                className="orenda-btn-ghost"
                disabled={page >= totalPages || phase === "loading"}
                onClick={() => load(page + 1)}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      {stmtOpen && (
        <StatementModal
          busy={stmtBusy}
          error={stmtError}
          onClose={() => {
            if (stmtBusy) return;
            setStmtOpen(false);
          }}
          onDownload={async ({ format, chosenMonth, year, startDate, endDate }) => {
            setStmtError(null);
            setStmtBusy(true);
            try {
              const resp = await orendaService.generateStatementOrenda({
                format,
                chosenMonth,
                year,
                startDate,
                endDate,
              });
              const dataURI = parseStatementResponse(resp);
              if (!dataURI) throw new Error("No statement data returned.");
              // Build a filename hint from the chosen range.
              const name =
                `statement-${chosenMonth || year || startDate || "range"}.${format}`;
              const a = document.createElement("a");
              a.href = dataURI;
              a.download = name;
              document.body.appendChild(a);
              a.click();
              document.body.removeChild(a);
              setStmtBusy(false);
              setStmtOpen(false);
            } catch (err) {
              setStmtBusy(false);
              setStmtError(
                err?.response?.data?.errorMessage ||
                  err?.message ||
                  "Couldn't generate that statement."
              );
            }
          }}
        />
      )}
    </section>
  );
};

// ─── Statement modal ──────────────────────────────────────────────

const StatementModal = ({ busy, error, onClose, onDownload }) => {
  const [format, setFormat] = useState("pdf");
  // range mode: "month" | "year" | "custom"
  const [rangeMode, setRangeMode] = useState("month");
  const [chosenMonth, setChosenMonth] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });
  const [year, setYear] = useState(() => new Date().getFullYear());
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [localErr, setLocalErr] = useState(null);

  const submit = () => {
    setLocalErr(null);
    if (rangeMode === "month" && !chosenMonth) {
      setLocalErr("Pick a month.");
      return;
    }
    if (rangeMode === "year" && (!year || Number.isNaN(Number(year)))) {
      setLocalErr("Pick a year.");
      return;
    }
    if (rangeMode === "custom" && (!startDate || !endDate)) {
      setLocalErr("Pick both a start and end date.");
      return;
    }
    const payload =
      rangeMode === "month"
        ? { format, chosenMonth }
        : rangeMode === "year"
        ? { format, year: Number(year) }
        : { format, startDate, endDate };
    onDownload(payload);
  };

  return (
    <div
      className="orenda-reveal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="orenda-stmt-title"
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) onClose?.();
      }}
    >
      <div className="orenda-reveal" style={{ maxWidth: 480 }}>
        <div className="orenda-reveal-head">
          <div>
            <div className="orenda-reveal-eyebrow">Statement</div>
            <h2 id="orenda-stmt-title" className="orenda-reveal-title">
              Download a statement
            </h2>
          </div>
          <button
            type="button"
            className="orenda-reveal-close"
            onClick={onClose}
            disabled={busy}
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <div className="orenda-reveal-body">
          <div className="orenda-benes-field">
            <label>Format</label>
            <div className="orenda-benes-segmented">
              <button
                type="button"
                className={format === "pdf" ? "is-active" : ""}
                onClick={() => setFormat("pdf")}
                disabled={busy}
              >
                PDF
              </button>
              <button
                type="button"
                className={format === "csv" ? "is-active" : ""}
                onClick={() => setFormat("csv")}
                disabled={busy}
              >
                CSV
              </button>
            </div>
          </div>

          <div className="orenda-benes-field">
            <label>Period</label>
            <div className="orenda-benes-segmented">
              <button
                type="button"
                className={rangeMode === "month" ? "is-active" : ""}
                onClick={() => setRangeMode("month")}
                disabled={busy}
              >
                A month
              </button>
              <button
                type="button"
                className={rangeMode === "year" ? "is-active" : ""}
                onClick={() => setRangeMode("year")}
                disabled={busy}
              >
                A year
              </button>
              <button
                type="button"
                className={rangeMode === "custom" ? "is-active" : ""}
                onClick={() => setRangeMode("custom")}
                disabled={busy}
              >
                Custom
              </button>
            </div>
          </div>

          {rangeMode === "month" && (
            <div className="orenda-benes-field">
              <label htmlFor="stmt-month">Month</label>
              <input
                id="stmt-month"
                type="month"
                value={chosenMonth}
                onChange={(e) => setChosenMonth(e.target.value)}
                disabled={busy}
              />
            </div>
          )}

          {rangeMode === "year" && (
            <div className="orenda-benes-field">
              <label htmlFor="stmt-year">Year</label>
              <input
                id="stmt-year"
                type="number"
                min={2020}
                max={2100}
                value={year}
                onChange={(e) => setYear(e.target.value)}
                disabled={busy}
              />
            </div>
          )}

          {rangeMode === "custom" && (
            <div className="orenda-reveal-grid">
              <div className="orenda-benes-field">
                <label htmlFor="stmt-start">Start</label>
                <input
                  id="stmt-start"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  disabled={busy}
                />
              </div>
              <div className="orenda-benes-field">
                <label htmlFor="stmt-end">End</label>
                <input
                  id="stmt-end"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  disabled={busy}
                />
              </div>
            </div>
          )}

          {(error || localErr) && (
            <div
              className="orenda-reveal-countdown"
              style={{ color: "#c04040" }}
            >
              {localErr || error}
            </div>
          )}

          <div className="orenda-benes-form-actions">
            <button
              type="button"
              className="orenda-btn-ghost"
              onClick={onClose}
              disabled={busy}
            >
              Cancel
            </button>
            <button
              type="button"
              className="orenda-btn-primary"
              onClick={submit}
              disabled={busy}
            >
              {busy ? "Generating…" : "Download"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const StatementIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="12" y1="18" x2="12" y2="12" />
    <polyline points="9 15 12 18 15 15" />
  </svg>
);

export default OrendaTransactions;
